import {
  ConfirmedOrderListQueryVariables,
  OrderFilterInput,
  TripOrdersQuery,
  useConfirmedOrderListQuery,
  useDispatchesForDateLazyQuery,
  useSummaryAllOrderItemsQuery,
  useSummaryAllOrdersQuery,
  useTripOrdersLazyQuery,
} from "generated/graphql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import { uniqBy } from "lodash-es"
import { useEffect } from "react"
import { useParams } from "react-router-dom"
import { useCurrentDc } from "store/auth.redux"
import { useTotalOrders } from "store/dispatch.redux"
import { useFilter } from "store/search-filter/search-filter.reducer"
import { getId } from "utils/custom.utils"

// DISPATCHES_FOR_DATE -> tripsForDate
export const useDispatchesForDate = () => {
  const [dc] = useCurrentDc()
  const [{ date, search, page }] = useSearchFilter()
  const [getDispatches, response] = useDispatchesForDateLazyQuery()

  useEffect(() => {
    getDispatches({
      variables: {
        first: 15,
        skip: page * 15,
        distributionCentreId: dc?.id,
        date,
        query: search,
      },
    })
  }, [dc, date, getDispatches, search, page])
  return response
}

// OrderFilterInputVariables

export const useOrderFilterVariables = (): OrderFilterInput => {
  const [orderFilter] = useFilter("orders")
  return {
    deliveryStatus: orderFilter?.deliveryStatus
      ?.map((a: any) => a?.id)
      ?.filter((a: any) => a),
    paymentStatus: orderFilter?.paymentStatus?.map((a: any) => a?.id),
    orderStatus: orderFilter?.orderStatus?.map((a: any) => a?.id),
    date_Lte: orderFilter?.dateRange?.[1],
    date_Gte: orderFilter?.dateRange?.[0],
    placedById: orderFilter?.placedById?.map((a: any) => a?.id),
    distributionCentreIds: orderFilter?.distributionCentreIds?.map(
      (d: any) => d.id
    ),
    paymentMethods: orderFilter?.paymentMethods?.map((a: any) => a?.id),
    placedInId: orderFilter?.placedInId?.id,
    salesmanId: orderFilter?.salesmanId?.id,
    orderCategories: orderFilter?.orderCategories?.map((c: any) => c?.id),
  }
}

export const useEcommerceOrderVariables =
  (): ConfirmedOrderListQueryVariables => {
    const [filter] = useFilter("dispatch")
    const date = filter?.dateRange
    const [{ search }] = useSearchFilter()
    return {
      query: search,

      sellableItemIds: filter?.sellableItemIds?.map(getId),
      filter: {
        placedInId: filter?.placedInId?.id,
        orderCategories: filter?.orderCategories?.map((c: any) => c?.id),
        date_Lte: date?.[1],
        date_Gte: date?.[0],
        orderStatus: filter?.orderStatus
          ?.map((a: any) => a?.id)
          ?.filter((a: any) => a),
        placedById: filter?.placedById?.map(getId),
        distributionCentreIds: filter?.distributionCentreIds?.map(
          (d: any) => d.id
        ),
        pincodes: filter?.pincodes?.map((d: any) => d.id),
      },
    }
  }
// CONFIRMED_ORDER_LIST -> allOrders
export const useDispatchOrdersList = () => {
  const variables = useEcommerceOrderVariables()
  const orders = useTotalOrders()

  const { data, loading } = useConfirmedOrderListQuery({
    variables,
  })

  return {
    // data: unionWith(orders, data?.allOrders, (o, i) => o?.id === i?.id),
    data: uniqBy(data?.allOrders?.concat(orders), o => o?.id)?.map(
      o => orders?.find(ord => ord.id === o?.id) ?? o
    ),
    loading,
  }
}

// TRIP_ORDERS -> tripDetails
export const useTripOrders = (
  onCompleted?: (data: TripOrdersQuery) => void
) => {
  const { id } = useParams<{ id: string }>()
  const tripId = parseInt(id)
  const [getTripDetail, { loading, data }] = useTripOrdersLazyQuery({
    onCompleted,
  })

  useEffect(() => {
    if (tripId) getTripDetail({ variables: { id: tripId } })
  }, [tripId, getTripDetail])

  return { loading, data }
}

// Summary of all the orders
export const useSummaryAllOrders = () => {
  const variables = useEcommerceOrderVariables()
  return useSummaryAllOrdersQuery({
    variables: {
      ...variables,
      filter: {
        ...variables.filter,
        orderStatus: undefined,
      },
    },
  })
}

export const useSummaryAllOrderItems = (isOpen = false) => {
  const variables = useOrderFilterVariables()
  return useSummaryAllOrderItemsQuery({
    variables: {
      filter: variables,
    },
    fetchPolicy: !isOpen ? "cache-only" : "cache-and-network",
    skip:
      !isOpen ||
      !variables?.orderCategories ||
      variables?.orderCategories?.length === 0,
  })
}
